import React, { useState } from 'react'
import { Alert, Button, Card, Descriptions, Input, Popconfirm, Result, Spin, Table, Tag, message } from 'antd'
import { DeleteOutlined, SearchOutlined } from '@ant-design/icons'
import { get } from 'lodash'
import moment from 'moment'
import 'moment/locale/th'
import Layout from '../Home'
import { BOOKING_DOCTOR_STATUS } from '../../constant'
import { formatHn, toRawHn } from '../../utils/hn'
import {
  deleteBooking,
  findPatientByCidOrHn,
  getPatientBookings,
  getTelemedPatientId,
} from '../../apiServices/bookingServices'
import { minuteOfWeekToLabel } from '../CallCenterBooking/utils'

const GREEN = '#0A905F'

// สถานะที่ยังไม่จบกระบวนการ ไฮไลต์ไว้ให้เห็นว่าอันไหนยัง "มีชีวิต" อยู่ในระบบ
const ACTIVE_STATUS = ['PATIENT_DRAFT', 'DOCTOR_PENDING', 'DOCTOR_CONFIRM', 'STAFF_PENDING', 'STAFF_CONFIRM']

const Content = ({ userInfo }) => {
  const [searchInput, setSearchInput] = useState('')
  const [searching, setSearching] = useState(false)
  const [patient, setPatient] = useState(null)
  const [patientError, setPatientError] = useState(null)
  const [bookings, setBookings] = useState([])
  const [loadingBookings, setLoadingBookings] = useState(false)
  const [deletingId, setDeletingId] = useState(null)

  const loadBookings = async (patientId) => {
    setLoadingBookings(true)
    try {
      setBookings(await getPatientBookings(patientId))
    } catch (error) {
      message.error('ไม่สามารถโหลดรายการนัดหมายได้')
      setBookings([])
    } finally {
      setLoadingBookings(false)
    }
  }

  const onSearch = async () => {
    const keyword = String(searchInput || '').trim()
    if (!keyword) {
      message.warning('กรุณากรอกเลขบัตรประชาชนหรือ HN ของผู้ป่วย')
      return
    }
    setSearching(true)
    setPatient(null)
    setPatientError(null)
    setBookings([])
    try {
      // รับ HN แบบ 28318/66 ที่เจ้าหน้าที่ใช้ แล้วแปลงเป็นรูปแบบในฐานข้อมูล
      const matches = await findPatientByCidOrHn(toRawHn(keyword))
      const info = matches.find((item) => item && item.userId)
      if (!info) {
        setPatientError('ไม่พบผู้ป่วยที่ใช้เลขบัตรประชาชนหรือ HN นี้')
        return
      }
      if (matches.length > 1) {
        message.warning(`พบผู้ป่วยที่ตรงกัน ${matches.length} ราย แสดงรายการแรก`)
      }
      const patientId = await getTelemedPatientId(info.userId)
      if (!patientId) {
        setPatientError('ผู้ป่วยรายนี้ยังไม่เคยเข้าใช้งานในแอปพลิเคชัน จึงยังไม่มีนัดหมายในระบบเทเลเมด')
        return
      }
      const resolved = {
        everOmaId: info.userId,
        // เก็บเป็นตัวเลขให้ตรงกับที่ Booking เก็บไว้ เหมือนตอนสร้างนัด
        patientId: Number(patientId),
        cId: info.cId || '',
        hnId: info.hnId || '',
        name: [info.firstname, info.lastname].filter(Boolean).join(' '),
      }
      setPatient(resolved)
      await loadBookings(resolved.patientId)
    } catch (error) {
      const status = get(error, 'response.status')
      if (status === 401 || status === 403) {
        setPatientError('บัญชีของคุณไม่มีสิทธิ์ค้นหาข้อมูลผู้ป่วย')
      } else {
        setPatientError('เกิดข้อผิดพลาดในการค้นหาผู้ป่วย กรุณาลองใหม่อีกครั้ง')
      }
    } finally {
      setSearching(false)
    }
  }

  const onDelete = async (booking) => {
    setDeletingId(booking.id)
    try {
      await deleteBooking(booking.id)
      message.success('ลบนัดหมายเรียบร้อยแล้ว')
      await loadBookings(patient.patientId)
    } catch (error) {
      const status = get(error, 'response.status')
      if (status === 401 || status === 403) {
        message.error('บัญชีของคุณไม่มีสิทธิ์ลบนัดหมาย (ต้องเป็น admin)')
      } else {
        message.error('ลบนัดหมายไม่สำเร็จ กรุณาลองใหม่อีกครั้ง')
      }
    } finally {
      setDeletingId(null)
    }
  }

  // userInfo เริ่มต้นเป็น {} แล้วค่อยถูกเติมจาก redux ระหว่างนั้นยังตัดสินสิทธิ์ไม่ได้
  if (!userInfo || !Array.isArray(userInfo.roles)) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    )
  }
  if (!userInfo.roles.map((item) => item.name).includes('admin')) {
    return <Result status="403" title="ไม่มีสิทธิ์เข้าถึง" subTitle="หน้านี้สำหรับผู้ดูแลระบบเท่านั้น" />
  }

  const columns = [
    {
      title: 'วันที่นัด',
      dataIndex: 'admitTime',
      width: 170,
      render: (value) => (value ? moment(value).format('ddd D MMM YYYY') : '-'),
    },
    {
      title: 'เวลา',
      dataIndex: 'bookingTime',
      width: 90,
      render: (value) => (typeof value === 'number' ? `${minuteOfWeekToLabel(value)} น.` : '-'),
    },
    {
      title: 'ผู้ให้บริการ',
      dataIndex: 'doctor',
      render: (doctor, row) => get(doctor, 'fullname') || `doctorId ${row.doctorId}`,
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      width: 220,
      render: (value) => (
        <Tag color={ACTIVE_STATUS.includes(value) ? 'green' : 'default'}>
          {BOOKING_DOCTOR_STATUS[value] || value || '-'}
        </Tag>
      ),
    },
    {
      title: 'จองผ่าน',
      dataIndex: 'bookingDevice',
      width: 260,
      render: (value) => value || '-',
    },
    {
      title: 'Booking ID',
      dataIndex: 'id',
      width: 220,
      render: (value) => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{value}</span>,
    },
    {
      title: 'จัดการ',
      key: 'action',
      width: 110,
      render: (_, row) => (
        <Popconfirm
          title={
            <div style={{ maxWidth: 280 }}>
              ลบนัดหมายนี้ถาวร?
              <div style={{ color: '#888' }}>
                {moment(row.admitTime).format('D MMM YYYY')}{' '}
                {typeof row.bookingTime === 'number' ? minuteOfWeekToLabel(row.bookingTime) : ''} น. — กู้คืนไม่ได้
              </div>
            </div>
          }
          okText="ลบ"
          okType="danger"
          cancelText="ยกเลิก"
          onConfirm={() => onDelete(row)}
        >
          <Button danger size="small" icon={<DeleteOutlined />} loading={deletingId === row.id}>
            ลบ
          </Button>
        </Popconfirm>
      ),
    },
  ]

  return (
    <div style={{ padding: 24, width: '100%' }}>
      <h1 style={{ color: GREEN, marginBottom: 2 }}>นัดหมายเทเลเมด (admin)</h1>
      <div style={{ color: '#888', marginBottom: 16 }}>
        ค้นผู้ป่วยเพื่อดูนัดหมายในระบบเทเลเมดทั้งหมด และลบนัดที่สร้างไว้สำหรับทดสอบออกได้
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Input.Search
          style={{ maxWidth: 520 }}
          placeholder="เลขบัตรประชาชน หรือ HN (เช่น 28318/66)"
          enterButton={
            <span>
              <SearchOutlined /> ค้นหา
            </span>
          }
          size="large"
          value={searchInput}
          loading={searching}
          onChange={(e) => setSearchInput(e.target.value)}
          onSearch={onSearch}
        />
        {patientError ? <Alert style={{ marginTop: 12 }} type="error" showIcon message={patientError} /> : null}
        {patient ? (
          <Descriptions style={{ marginTop: 16 }} bordered size="small" column={4}>
            <Descriptions.Item label="ชื่อ-นามสกุล">{patient.name || '-'}</Descriptions.Item>
            <Descriptions.Item label="HN">{formatHn(patient.hnId) || '-'}</Descriptions.Item>
            <Descriptions.Item label="เลขบัตรประชาชน">{patient.cId || '-'}</Descriptions.Item>
            <Descriptions.Item label="patientId (เทเลเมด)">{patient.patientId}</Descriptions.Item>
          </Descriptions>
        ) : null}
      </Card>

      {patient ? (
        <Card title={`รายการนัดหมาย (${bookings.length})`}>
          <Alert
            style={{ marginBottom: 12 }}
            type="warning"
            showIcon
            message="การลบเป็นการลบถาวร กู้คืนไม่ได้ และผู้ป่วยจะไม่เห็นนัดนี้ในแอปอีก"
          />
          <Table
            rowKey="id"
            columns={columns}
            dataSource={bookings}
            loading={loadingBookings}
            size="small"
            pagination={{ pageSize: 20, showSizeChanger: false }}
            scroll={{ x: 1100 }}
            locale={{ emptyText: 'ผู้ป่วยรายนี้ยังไม่มีนัดหมายในระบบเทเลเมด' }}
          />
        </Card>
      ) : null}
    </div>
  )
}

const AdminBookingLookup = (props) => <Layout {...props} render={({ userInfo }) => <Content userInfo={userInfo} />} />

export default AdminBookingLookup
